/* @flow */

import { createSelector } from 'reselect';
import type { State } from '../../../types/redux';

// Base selectors - get specific properties to avoid returning entire objects
export const selectTagsState = (state: State) => state.tags || {};
export const selectTagsArray = createSelector(
  selectTagsState,
  tagsState => tagsState.tags || []
);
export const selectTagPickerState = createSelector(
  selectTagsState,
  tagsState => tagsState.isTagPickerOpen || false
);
export const selectActiveCustomerId = createSelector(
  selectTagsState,
  tagsState => tagsState.activeCustomerId || null
);
export const selectTagSortInfo = createSelector(
  selectTagsState,
  tagsState => tagsState.sortInfo || { field: 'name', direction: 'asc' }
);

// Select all the tags from the store
export const selectAllTags = selectTagsArray;

// Select the feed requests from the feed state
export const selectFeedRequests = (state: State) => {
  if (!state.feed || !state.feed.requests) {
    return [];
  }

  return state.feed.requests;
};

// Select if the tag picker is open and has a valid customer
export const selectTagPickerIsOpenWithCustomer = createSelector(
  [selectTagPickerState, selectActiveCustomerId],
  (isOpen, customerId) => isOpen && Boolean(customerId)
);

// Select a specific tag by ID
export const selectTagById = createSelector(
  [selectTagsArray, (_, tagId) => tagId],
  (tags, tagId) => tags.find(tag => tag._id === tagId)
);

// Select tags for a specific customer
export const selectTagsForCustomer = createSelector(
  [
    selectAllTags,
    selectFeedRequests,
    (_, customerId) => customerId
  ],
  (allTags, requests, customerId) => {
    // Find the customer in the requests
    const customer = requests.find(req => 
      req.lookupTenant && req.lookupTenant._id === customerId
    )?.lookupTenant;
    
    if (!customer || !customer.Tags) {
      return [];
    }
    
    // Map customer tag IDs to full tag objects
    return customer.Tags
      .map(tagId => allTags.find(tag => tag._id === tagId))
      .filter(Boolean); // Remove undefined values
  }
);